import React from 'react';
import { Button, createStyles, Grid, Icon, IconButton, makeStyles, Paper,Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Theme, Typography } from '@material-ui/core';
import { KeyboardTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import ptBR from 'date-fns/locale/pt-BR';

import { CostTimeModel, costTimeModelInitial } from '../../../Models/CostTimeModel';
import { VehicleContext } from '../VehicleContext';
import { numberFormat, stringToTime } from '../../../shared/operators';
import { BasicConfirmDialog } from '../../../shared/components/BasicConfirmDialog';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { HomeContext } from '../../Home/HomeContext';
import { EditAction } from '../../../shared/enum/EditAction';

const useStyles = makeStyles((theme: Theme) => createStyles({
    itens: {   
        width: '100%',
        color: 'primary',   
    },
    paper:{
        padding: theme.spacing(3),   
    },
    tableResume: {
        backgroundColor: theme.palette.background.default
    },
    addButton: {
        paddingRight: theme.spacing(1.5)
    } 
}));

export function CostTime() {
    const classes = useStyles();
    
    const vehicleContext = React.useContext(VehicleContext);
    const homeContext = React.useContext(HomeContext);
    
    const [costTimeCurrent, setCostTimeCurrent] = React.useState<CostTimeModel>(costTimeModelInitial);
    const [finalHour, setFinalHour] = React.useState<Date | null>(null);
    const [initialHour, setInitialHour] = React.useState<Date | null>(null);

    const [timeReachNotDefinedError, setTimeReachNotDefinedError] = React.useState<boolean>(false);
    const [timePriceNotDefinedError, setTimePriceNotDefinedError] = React.useState<boolean>(false);
    const [timeInitialNotDefinedError, setTimeInitialNotDefinedError] = React.useState<boolean>(false);
    const [timeFinalNotDefinedError, setTimeFinalNotDefinedError] = React.useState<boolean>(false);

    const [editIndex, setEditIndex] = React.useState<number | null>(null);
    const [deleteIndex, setDeleteIndex] = React.useState<number | null>(null);

    const [isCostTimeDeleteDialogOpen, setIsCostTimeDeleteDialogOpen] = React.useState<boolean>(false);
    const [edit, setEdit] = React.useState(false);

    React.useEffect(() => {
        if(homeContext.pageLocation === PageLocation.VEHICLE_EDIT) {
            setEdit(true);
        } else {
            setEdit(false);
        }
    }, []);

    const handleCostTimeNumberChange = (prop: keyof CostTimeModel) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const number = numberFormat(event.target.value);
        setCostTimeCurrent({...costTimeCurrent, [prop]: number});
    };

    const hasError = () => {
        const timeReach = !((costTimeCurrent.reach != null) && (costTimeCurrent.reach >= 0));
        setTimeReachNotDefinedError(timeReach);

        const timePrice = !((costTimeCurrent.timePrice != null) && (costTimeCurrent.timePrice >= 0));
        setTimePriceNotDefinedError(timePrice);

        const timeInitial = !costTimeCurrent.initialTime || costTimeCurrent.initialTime.includes('invalid');
        setTimeInitialNotDefinedError(timeInitial);

        const timeFinal = !costTimeCurrent.finalTime || costTimeCurrent.finalTime.includes('invalid');
        setTimeFinalNotDefinedError(timeFinal);

        return timeReach || timePrice || timeInitial || timeFinal;
    };

    const handleAdd = () => {
        if(!hasError()){
            const vehicleCostTime = vehicleContext.vehicle.costTime ? vehicleContext.vehicle.costTime : [];

            if(editIndex != null) {
                if(edit)
                    costTimeCurrent.editAction = EditAction.UPDATE;
                vehicleCostTime[editIndex] = costTimeCurrent;
                setEditIndex(null);
            } else {
                if(edit)
                    costTimeCurrent.editAction = EditAction.INSERT;
                vehicleCostTime.push(costTimeCurrent);
            }

            vehicleContext.setVehicle({...vehicleContext.vehicle, costTime : vehicleCostTime});
            
            setCostTimeCurrent(costTimeModelInitial);
            setFinalHour(null);
            setInitialHour(null);
        }
    };

    const handleCostTimeEdit = (costTime : CostTimeModel) => {
        setCostTimeCurrent(costTime);
        setInitialHour(stringToTime(costTime.initialTime));
        setFinalHour(stringToTime(costTime.finalTime));
    };

    const handleCostTimeDelete = () => {
        if(deleteIndex != null){
            if(edit && vehicleContext.vehicle && 
                vehicleContext.vehicle.costTime && vehicleContext.vehicle.costTime[deleteIndex].editAction != EditAction.INSERT) {
                const vehicleCostTimeList = [...vehicleContext.vehicle.costTime];
                vehicleCostTimeList[deleteIndex].editAction = EditAction.DELETE;
                vehicleContext.setVehicle({...vehicleContext.vehicle, costTime : vehicleCostTimeList});
                setIsCostTimeDeleteDialogOpen(false);
            }
            else {
                const costTimeList = vehicleContext.vehicle.costTime ? vehicleContext.vehicle.costTime : [];
                costTimeList.splice(deleteIndex, 1);
                vehicleContext.setVehicle({...vehicleContext.vehicle, costTime : costTimeList});
                setIsCostTimeDeleteDialogOpen(false);
            }
        }    
    };

    const getTitle = () => {
        return (
            <Grid item xs={12}>
                <Typography variant="h6">
                Custo tempo
                </Typography>
            </Grid>
        );
    };

    const getReach = () => {
        return (
            <Grid item xs={12} sm={6} md={3}>
                <TextField 
                    id='vehicle-time-reach'
                    fullWidth
                    label="Alcance"
                    variant="outlined"
                    type="number"
                    className={ classes.itens }  
                    value={ costTimeCurrent.reach ? costTimeCurrent.reach : (costTimeCurrent.reach === 0 ? 0 : '') }
                    onChange = { handleCostTimeNumberChange('reach') }
                    error={ timeReachNotDefinedError && !((costTimeCurrent.reach != null) && (costTimeCurrent.reach >= 0)) }
                    helperText={ (timeReachNotDefinedError && !((costTimeCurrent.reach != null) && (costTimeCurrent.reach >= 0)))
                    && 'Campo obrigatório' }
                />
            </Grid>
        );
    };

    const getPrice = () => {
        return (
            <Grid item xs={12} sm={6} md={3}>
                <TextField 
                    id='vehicle-time-price'
                    fullWidth
                    label="Valor tempo"
                    variant="outlined"
                    type="number"
                    className={ classes.itens }  
                    value={ costTimeCurrent.timePrice ? costTimeCurrent.timePrice : (costTimeCurrent.timePrice === 0 ? 0 : '') }
                    onChange = { handleCostTimeNumberChange('timePrice') }
                    error={ timePriceNotDefinedError && !((costTimeCurrent.timePrice != null) && (costTimeCurrent.timePrice >= 0)) }
                    helperText={ (timePriceNotDefinedError && !((costTimeCurrent.timePrice != null) && (costTimeCurrent.timePrice >= 0)))
                    && 'Campo obrigatório' }
                />
            </Grid>
        );
    };

    const getTimes = () => {
        return (
            <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>      
                { getInitialTime() }
                { getFinalTime() }
            </MuiPickersUtilsProvider>
        );
    };

    const getInitialTime = () => {
        return (
            <Grid item xs={12} sm={6} md={3}>
                <KeyboardTimePicker
                    autoOk
                    ampm={ false }
                    openTo="hours"
                    views={ ['hours', 'minutes', 'seconds'] }
                    format="HH:mm:ss"
                    label="Hora inicial"
                    variant="inline"
                    inputVariant="outlined"
                    value={ initialHour }
                    onChange={(date) => { 
                        setInitialHour(date); 

                        if (date && Object.prototype.toString.call(date) === '[object Date]' && !isNaN(date.getTime()))
                            setCostTimeCurrent({...costTimeCurrent, initialTime: date.toLocaleTimeString('pt-Br')});
                        else 
                            setCostTimeCurrent({...costTimeCurrent, initialTime: 'invalid'});
                    }}
                    className={ classes.itens }
                    keyboardIcon={ <Icon>timer</Icon> }
                    error={ timeInitialNotDefinedError && (!costTimeCurrent.initialTime || costTimeCurrent.initialTime.includes('invalid')) }
                    helperText={ timeInitialNotDefinedError
                    && (!costTimeCurrent.initialTime || costTimeCurrent.initialTime.includes('invalid'))
                        ? (costTimeCurrent.initialTime.includes('invalid')
                            ? 'Horario invalida'
                            : 'Campo obrigatório')
                        : '' }
                />
            </Grid>
        );
    };

    const getFinalTime = () => {
        return (
            <Grid item xs={12} sm={6} md={3}>
                <KeyboardTimePicker
                    autoOk
                    ampm={false}
                    openTo="hours"
                    views={['hours', 'minutes', 'seconds']}
                    format="HH:mm:ss"
                    label="Hora final"
                    variant="inline"
                    inputVariant="outlined"
                    value={finalHour}
                    onChange={(date) => { 
                        setFinalHour(date); 

                        if (date && Object.prototype.toString.call(date) === '[object Date]' && !isNaN(date.getTime()))
                            setCostTimeCurrent({...costTimeCurrent, finalTime: date.toLocaleTimeString('pt-Br')});
                        else 
                            setCostTimeCurrent({...costTimeCurrent, finalTime: 'invalid'});       
                    }}
                    className={ classes.itens }
                    error={ timeFinalNotDefinedError && (!costTimeCurrent.finalTime || costTimeCurrent.finalTime.includes('invalid')) }
                    helperText={ timeFinalNotDefinedError
                        && (!costTimeCurrent.finalTime || costTimeCurrent.finalTime.includes('invalid'))
                        ? (costTimeCurrent.finalTime.includes('invalid')
                            ? 'Hora invalida'
                            : 'Campo obrigatório')
                        : '' }
                    keyboardIcon={ <Icon>timer</Icon> }
                />
            </Grid>
        );
    };

    const getAddButton = () => {
        return (
            <Grid 
                container 
                direction="column"
                justify="flex-end"
                alignItems="flex-end"
                className={classes.addButton}>
                <Grid item>
                    <Button 
                        variant="contained" 
                        color='primary'
                        onClick={ handleAdd } 
                        size='large'>
                        { (editIndex != null) ? 'Salvar' : 'Adicionar' }
                    </Button>
                </Grid>
            </Grid>
        );
    };

    const getTimeCostList = () => {
        return <>
            { vehicleContext.vehicle.costTime &&  
              vehicleContext.vehicle.costTime.length > 0 && 
              vehicleContext.vehicle.costTime.find(c => c.editAction != EditAction.DELETE) &&
            <Grid item xs={12}>
                <Typography variant="subtitle1">Resumo do custo tempo</Typography>
                <TableContainer component={Paper} elevation={0} className={classes.tableResume}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">Alcance</TableCell>
                                <TableCell align="center">Valor tempo</TableCell>
                                <TableCell align="center">Hora inicial</TableCell>
                                <TableCell align="center">Hora final</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {vehicleContext.vehicle.costTime.slice(0).reverse().map((costTime, index, array) => (
                                costTime.editAction != EditAction.DELETE && (
                                    <TableRow key={index}>
                                        <TableCell align="center">
                                            {costTime.reach
                                                ? costTime.reach
                                                : (costTime.reach === 0 ? 0 : '-')}
                                        </TableCell>
                                        <TableCell align="center">
                                            {costTime.timePrice
                                                ? costTime.timePrice
                                                : (costTime.timePrice === 0 ? 0 : '-')}
                                        </TableCell>
                                        <TableCell align="center">
                                            {costTime.initialTime
                                                ? costTime.initialTime
                                                : '-'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {costTime.finalTime
                                                ? costTime.finalTime
                                                : '-'}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => { handleCostTimeEdit(costTime); setEditIndex(array.length - 1 -index); }}>
                                                <Icon>edit</Icon>
                                            </IconButton>
                                            <IconButton size="small" onClick={() => { setIsCostTimeDeleteDialogOpen(true); setDeleteIndex(array.length - 1 - index); }}>
                                                <Icon>delete_outline</Icon>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                )
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid> }
        </>;
    };

    const getDeleteDialog = () => {
        return (
            <BasicConfirmDialog
                title=''
                description="Tem certeza que deseja excluir este custo tempo?"
                handleFirstButton={ () => handleCostTimeDelete() }
                handleSecondButton={ () => setIsCostTimeDeleteDialogOpen(false) }
                textFirstButton="Excluir"
                textSecondButton="Cancelar"
                open={isCostTimeDeleteDialogOpen} />
        );
    };
    
    return (
        <Grid item xs={12} className={classes.itens}>
            <Paper className={classes.paper}>
                <Grid container spacing={3}>
                    { getTitle() }
                    { getReach() }
                    { getPrice() }
                    { getTimes() }
                    { getAddButton() }
                </Grid> 
                { getTimeCostList() }
            </Paper>
            { getDeleteDialog() }
        </Grid>
    );
}